import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success('Email enviado');
    } catch (error) {
      toast.error('Error al enviar email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-hero-glow opacity-30 blur-3xl" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-12 w-full max-w-md relative z-10"
      >
        <div className="flex items-center justify-center space-x-2 mb-8">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-ai-glow to-brand-meta" />
          <span className="font-heading font-bold text-xl">AdPulse AI</span>
        </div>

        {sent ? (
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-success/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-success" />
            </div>
            <h2 className="font-heading font-bold text-2xl mb-4">¡Email Enviado!</h2>
            <p className="text-secondary mb-6">
              Si el email existe en nuestro sistema, recibirás un enlace para restablecer tu contraseña.
            </p>
            <p className="text-secondary text-sm mb-6">
              Revisa tu bandeja de entrada y spam.
            </p>
            <Link to="/login">
              <Button className="w-full bg-white text-black hover:bg-gray-200 rounded-full h-12">
                Volver al Login
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <h2 className="font-heading font-bold text-2xl text-center mb-2">¿Olvidaste tu contraseña?</h2>
            <p className="text-secondary text-center text-sm mb-8">
              Ingresa tu email y te enviaremos un enlace para restablecerla
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label className="font-mono text-xs uppercase tracking-widest text-secondary">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary" />
                  <Input
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-white/5 border-white/10 focus:border-white/30 text-white placeholder:text-white/20 rounded-lg h-12 pl-12"
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-ai-glow to-brand-meta text-white hover:opacity-90 rounded-full h-12"
                disabled={loading}
                data-testid="forgot-submit-button"
              >
                {loading ? 'Enviando...' : 'Enviar enlace'}
              </Button>
            </form>

            <Link 
              to="/login" 
              className="flex items-center justify-center space-x-2 text-secondary hover:text-primary text-sm mt-6"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Volver al login</span>
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
